import React, { useEffect, useState, useRef } from 'react';

import { Link, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { showToast } from '../utils/toast';
import { trackProductView } from '../api/productViews';
import { fetchCreatorPlanStatus, type PlanStatus } from '../utils/creatorPlan';
import './SalesPage.css';
import SEO from './SEO';

// Declare PayPal on window object
declare global {
  interface Window {
    paypal: any;
  }
}

interface ProductData {
  id: string;
  thumbnail: string;
  additional_images: string[];
  title: string;
  description: string;
  price: number;
  Type: string;
  category: string;
  access_url: string;
  creator_id?: string; // Make creator_id optional
  payment_type?: string; // Add this line
  slug: string; // Add slug property
}

const SalesPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<ProductData | null>(null);
  const [creator, setCreator] = useState<{ fullName: string; email: string; paypalEmail?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [paypalLoading, setPaypalLoading] = useState(true);
  const [paypalError, setPaypalError] = useState<string | null>(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showFreeProductModal, setShowFreeProductModal] = useState(false);
  const [freeProductEmail, setFreeProductEmail] = useState('');
  const paypalButtonRef = useRef<HTMLDivElement>(null);
  const paypalButtonRendered = useRef(false);
  const paypalButtonBarrier = useRef<HTMLDivElement>(null);
  const paypalButtonContainer = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const productFetched = useRef(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Creator's plan + revenue usage. Drives whether checkout is available.
  const [creatorPlan, setCreatorPlan] = useState<PlanStatus | null>(null);

  // Free-plan creators that hit the revenue cap cannot take payments.
  const isCreatorCapped = !!creatorPlan?.isLimitReached;
  
  // Get all images (thumbnail + additional images)
  const getAllImages = () => {
    if (!product) return [];
    const images = [product.thumbnail];
    if (product.additional_images && Array.isArray(product.additional_images)) {
      images.push(...product.additional_images);
    }
    return images;
  };
  
  const allImages = getAllImages();
  
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === allImages.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? allImages.length - 1 : prevIndex - 1
    );
  };
  
  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product]);

  const scrollToPayment = () => {
    paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Function to open the checkout modal
  const openCheckoutModal = () => {
    // Guard: the button is hidden when capped, this stops any other path in.
    if (isCreatorCapped) {
      showToast.error('This seller is not currently accepting payments.');
      return;
    }
    setShowCheckoutModal(true);
  };
  
  // Function to open the free product modal
  const openFreeProductModal = () => {
    if (isCreatorCapped) {
      showToast.error('This seller is not currently accepting payments.');
      return;
    }
    setShowFreeProductModal(true);
  };
  
  // Function to close the free product modal
  const closeFreeProductModal = () => {
    setShowFreeProductModal(false);
    setFreeProductEmail('');
  };
  
  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Handle free product checkout
  const handleFreeProductCheckout = async () => {
    if (!freeProductEmail || !isValidEmail(freeProductEmail)) {
      showToast.error('Please enter a valid email address');
      return;
    }
    
    try {
      // Record the free product purchase
      const orderId = await recordFreeProductPurchase(freeProductEmail);
      
      if (orderId) {
        showToast.success('🎉 Access granted! Sending confirmation email...');
        
        // Send confirmation email to the buyer for free products
        const baseUrl = window.location.origin;
        
        await sendPurchaseEmail(
          freeProductEmail,
          product?.title || 'Digital Product',
          product?.thumbnail || `${baseUrl}/logo.svg.png`,
          product?.access_url || '/',
          0, // Price is 0 for free products
          'Free',
          orderId
          // No creatorEmail passed, so creator won't be notified
        );

        closeFreeProductModal();
        
        // Redirect to product access URL
        setTimeout(() => {
          window.location.href = product?.access_url || '/';
        }, 2000);
      }
    } catch (error) {
      console.error('Error processing free product:', error);
      showToast.error('Failed to process free product. Please try again.');
    }
  };
  
  // Function to close the checkout modal
  const closeCheckoutModal = () => {
    setShowCheckoutModal(false);
    // Clean up PayPal button when closing modal
    if (paypalButtonRef.current) {
      paypalButtonRef.current.innerHTML = '';
    }
    paypalButtonRendered.current = false;
  };
  
  const calculateFakeOriginalPrice = (actualPrice: number) => {
    return (actualPrice * 2.5).toFixed(2); // 250% of actual price
  };
  
  const calculateDiscountedPrice = (originalPrice: number) => {
    return originalPrice.toFixed(2); // The actual price is now the discounted price
  };
  
  const displayPrice = () => {
    if (product?.payment_type === 'free') {
      return 'FREE';
    }
    return `$${calculateDiscountedPrice(product?.price || 0)}`;
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        
        // Improved query with better error handling
        if (!slug) {
          showToast.error('Invalid product URL');
          setProduct(null);
          return;
        }

        // Fetch product data - only non-deleted products
        const { data: productData, error: productError } = await supabase
          .from('digitalproducts')
          .select('id, thumbnail, additional_images, title, description, price, Type, category, access_url, creator_id, slug, payment_type')
          .eq('slug', slug)
          .eq('is_deleted', false) // Only fetch non-deleted products
          .limit(1)
          .single();

        if (productError) {
          console.error('Database error:', productError);
          if (productError.code === 'PGRST116') {
            showToast.error('Product not found');
            setProduct(null);
          } else if (productError.code === 'PGRST104') {
            showToast.error('Invalid product data');
            setProduct(null);
          } else {
            showToast.error('Error loading product: ' + productError.message);
            setProduct(null);
          }
          return;
        }

        if (!productData) {
          showToast.error('Product not found or has been hidden');
          setProduct(null);
          return;
        }

        // Fetch creator data
        const { data: creatorData, error: creatorError } = await supabase
          .from('profiles')
          .select('full_name, email, paypal_email')
          .eq('id', productData.creator_id)
          .single();

        if (creatorError) {
          console.error('Creator data error:', creatorError);
          // We'll still show the product even if we can't get creator info
        }

        setProduct(productData);
        if (creatorData) {
          setCreator({
            fullName: creatorData.full_name,
            email: creatorData.email,
            paypalEmail: creatorData.paypal_email
          });
        }

        // Check the creator's plan so checkout can be hidden when they are on
        // the free plan and have reached their revenue limit.
        fetchCreatorPlanStatus(productData.creator_id)
          .then(setCreatorPlan)
          .catch(() => setCreatorPlan(null));
        
        // Track the product view after successfully loading the product
        // Only track once per visit to avoid multiple counts
        if (!productFetched.current && productData?.id) {
          productFetched.current = true;
          
          // Attempt to get IP address using a public service
          // Since we're in browser context, we need to use a public API to get the real IP
          fetch('https://api.ipify.org?format=json')
            .then(response => response.json())
            .then(ipData => {
              // Get client information for tracking
              const clientInfo = {
                productId: productData.id,
                ipAddress: ipData.ip || null,
                userAgent: navigator.userAgent,
                referrer: document.referrer
              };
              
              // Track the view asynchronously
              trackProductView(clientInfo).catch(err => {
                console.warn('Failed to track product view:', err);
              });
            })
            .catch(error => {
              console.warn('Could not fetch IP address:', error);
              // Fallback: track without IP address
              const clientInfo = {
                productId: productData.id,
                ipAddress: null,
                userAgent: navigator.userAgent,
                referrer: document.referrer
              };
              
              trackProductView(clientInfo).catch(err => {
                console.warn('Failed to track product view:', err);
              });
            });
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        showToast.error('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  useEffect(() => {
    let mounted = true;
    let cleanupPayPal: (() => void) | null = null;

    const loadPayPalScript = () => {
      return new Promise<void>((resolve, reject) => {
        if (window.paypal) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://www.paypal.com/sdk/js?currency=USD&intent=capture&disable-funding=credit,card';
        script.async = true;
        script.onload = () => {
          if (mounted) {
            paypalButtonRendered.current = false;
            resolve();
          }
        };
        script.onerror = (error) => {
          console.error('PayPal script loading error:', error);
          reject(new Error('PayPal SDK failed to load. Please check your internet connection and try again.'));
        };
        document.body.appendChild(script);
      });
    };

    const initializePayPal = async () => {
      // Only initialize PayPal if we have a product and the modal is open
      if (!product || !paypalButtonRef.current || !mounted || paypalButtonRendered.current || !showCheckoutModal) return;

      try {
        setPaypalLoading(true);
        setPaypalError(null);

        await loadPayPalScript();

        if (!window.paypal || !mounted) {
          throw new Error('PayPal SDK not available');
        }

        // Clear any existing buttons
        if (paypalButtonRef.current) {
          paypalButtonRef.current.innerHTML = '';
        }

        // Create a protected container for PayPal button
        const protectedDiv = document.createElement('div');
        protectedDiv.style.position = 'relative';
        protectedDiv.style.zIndex = '1';
        paypalButtonRef.current.appendChild(protectedDiv);

        // Fetch creator's PayPal email
        const { data: creatorData, error: creatorError } = await supabase
          .from('profiles')
          .select('paypal_email, email, full_name')
          .eq('id', product.creator_id)
          .single();

        if (creatorError || !creatorData?.paypal_email) {
          throw new Error('Unable to process payment. Please contact the seller.');
        }

        // Update creator state if we got more information
        if (creatorData) {
          setCreator(prev => ({
            fullName: creatorData.full_name || prev?.fullName || 'Creator',
            email: creatorData.email || prev?.email || '',
            paypalEmail: creatorData.paypal_email
          }));
        }

        const buttons = window.paypal.Buttons({
          createOrder: (_data: any, actions: any) => {
            const orderAmount = parseFloat(product.price.toFixed(2));
            if (isNaN(orderAmount) || orderAmount <= 0) {
              throw new Error('Invalid price amount');
            }
            try {
              return actions.order.create({
                application_context: {
                  shipping_preference: 'NO_SHIPPING',
                  user_action: 'PAY_NOW',
                  brand_name: 'PokeShani'
                },
                purchase_units: [{
                  amount: {
                    currency_code: 'USD',
                    value: orderAmount.toString(),
                    breakdown: {
                      item_total: {
                        currency_code: 'USD',
                        value: orderAmount.toString()
                      }
                    }
                  },
                  items: [{
                    name: product.title,
                    unit_amount: {
                      currency_code: 'USD',
                      value: orderAmount.toString()
                    },
                    quantity: '1',
                    category: 'DIGITAL_GOODS'
                  }],
                  payee: {
                    email_address: creatorData.paypal_email
                  },
                  description: `PokeShani - ${product.title}`,
                  custom_id: `PokeShani-${product.id}`,
                  invoice_id: `PS-${Date.now()}-${product.id}`
                }]
              });
            } catch (error: any) {
              if (error.details?.[0]?.issue === 'PAYEE_ACCOUNT_RESTRICTED') {
                throw new Error('This merchant account is currently restricted. Please contact the seller.');
              }
              throw error;
            }
          },
          onApprove: async (data: any, actions: any) => {
            try {
              // Instead of capturing on frontend, we send the orderID to our backend
              // The backend will capture, record, and email
              const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
              
              const response = await fetch(`${apiUrl}/api/paypal/capture-order`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  orderId: data.orderID,
                  productId: product.id
                }),
              });

              const result = await response.json();

              if (result.success) {
                showToast.success('🎉 Purchase successful! Redirecting to your digital product...');
                setTimeout(() => {
                  window.location.href = result.accessUrl || product?.access_url || '/';
                }, 3000);
              } else {
                // Preserve the server's error code so the plan-limit case can be
                // shown with a friendlier message.
                const purchaseError: any = new Error(
                  result.error || 'Failed to process purchase'
                );
                purchaseError.code = result.code;
                throw purchaseError;
              }
            } catch (error: any) {
              console.error('Payment processing error:', error);
              if (error.code === 'CREATOR_PLAN_LIMITED') {
                showToast.error(
                  'Sorry, this seller is not currently accepting payments. Please try again later.'
                );
              } else {
                showToast.error(error.message || 'Payment failed. Please try again.');
              }
            }
          },
          onError: (err: Error) => {
            console.error('PayPal error:', err);
            const errorMessage = err.message.includes('merchant account is currently restricted')
              ? 'This seller\'s PayPal account is currently restricted. Please contact the seller for assistance.'
              : 'Failed to initialize PayPal. Please refresh the page and try again.';
            setPaypalError(errorMessage);
          }
        });

        if (mounted && paypalButtonRef.current) {
          await buttons.render(protectedDiv);
          paypalButtonRendered.current = true;
          cleanupPayPal = () => {
            if (paypalButtonRef.current) {
              paypalButtonRef.current.innerHTML = '';
            }
            paypalButtonRendered.current = false;
          };
        }

        setPaypalLoading(false);
      } catch (error: any) {
        if (mounted) {
          console.error('PayPal initialization error:', error);
          setPaypalError(error.message);
          setPaypalLoading(false);
          paypalButtonRendered.current = false;
        }
      }
    };

    if (product && showCheckoutModal) {
      initializePayPal();
    }

    return () => {
      mounted = false;
      if (cleanupPayPal) {
        cleanupPayPal();
      }
      if (paypalButtonRef.current) {
        paypalButtonRef.current.innerHTML = '';
      }
      paypalButtonRendered.current = false;
    };
  }, [product, showCheckoutModal]); // Update dependency array

  // Helper function to send purchase confirmation emails
  const sendPurchaseEmail = async (to: string, productTitle: string, productThumbnail: string, accessUrl: string, price?: number, paymentMethod?: string, orderId?: string, creatorEmail?: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      
      // Fallback to default logo if thumbnail is missing
      const finalThumbnail = productThumbnail || `${window.location.origin}/logo.svg.png`;
      
      // Send email directly with buyer's email and product details
      const response = await fetch(`${apiUrl}/api/send-purchase-email`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to,
          productTitle,
          productThumbnail: finalThumbnail,
          accessUrl,
          price,
          paymentMethod,
          orderId,
          creatorEmail
        })
      });

      if (!response.ok) {
        console.error('Failed to send confirmation email. Status:', response.status);
      }
    } catch (emailError: any) {
      console.error('Email sending error:', emailError);
      // Handle CORS error specifically
      if (emailError instanceof TypeError && emailError.message.includes('Failed to fetch')) {
        console.error('This might be a CORS error. Please check your server CORS configuration.');
      }
    }
  };

  // Record free product purchase without sending email
  const recordFreeProductPurchase = async (buyerEmail: string) => {
    try {
      if (!product) throw new Error('Product not found');

      // For free products, we'll record the purchase but skip email sending
      const purchaseData = {
        digitalproducts_id: product.id,
        creator_id: product.creator_id,
        price: 0, // Free product
        buyer_email: buyerEmail,
        payment_status: 'completed',
        created_at: new Date().toISOString(),
        title: product.title,
        payment_method: 'Free',
        order_id: `FREE-${Date.now()}`
      };

      console.log('Inserting free product purchase record:', purchaseData);

      // Add additional debugging
      console.log('Current Supabase user status:', await supabase.auth.getUser());
      
      const { data: purchaseResult, error: purchaseError } = await supabase
        .from('digitalproducts_purchases')
        .insert(purchaseData);

      if (purchaseError) {
        console.error('Free product purchase recording error:', purchaseError);
        showToast.error('Free product access granted but there was an issue recording it. Please contact support.');
        return null;
      } else {
        return purchaseData.order_id;
      }
    } catch (error) {
      console.error('Error recording free product purchase:', error);
      // Show error message but still redirect to avoid blocking the user
      showToast.error('Free product access granted but there was an issue recording it. Please contact support.');
      return null;
    }
  };

  if (loading) {
    return <div className="container py-5 text-center">Loading...</div>;
  }

  if (!product) {
    return <div className="container py-5 text-center">Product not found</div>;
  }

  return (
    <div className="sales-page-container">
      {/* Add dynamic SEO component for social sharing */}
      {product && (
        <SEO
          title={`${product.title} - Digital Product on PokeShani`}
          description={product.description.length > 160 ? product.description.substring(0, 157) + '...' : product.description}
          image={product.thumbnail}
          url={`https://pokeshani.com/sales/${product.slug}`}
          type="product"
          keywords={`${product.title}, digital product, ${product.category}, ${product.Type}, PokeShani, sell digital products`}
        />
      )}
      <div className="made-with-pokeshani" style={{ cursor: 'pointer', position: 'fixed', top: '20px', right: '20px', zIndex: 1000 }} onClick={() => window.open('https://pokeshani.com', '_blank')}>
        Made with PokeShani
      </div>
      
      <div className="sales-card">
        <div className="product-details">
          <h1 className="product-title">{product.title}</h1>
          
          {/* Image Carousel */}
          <div className="image-carousel-container">
            {allImages.length > 1 && (
              <button 
                className="carousel-nav-button prev-button" 
                onClick={prevImage}
                aria-label="Previous image"
              >
                &#8249;
              </button>
            )}
            
            <div className="carousel-image-wrapper">
              {allImages.length > 0 ? (
                <img
                  src={allImages[currentImageIndex]}
                  alt={`${product.title} - Image ${currentImageIndex + 1}`}
                  className="carousel-image"
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    maxWidth: 'none', 
                    maxHeight: 'none',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <div className="no-image-placeholder">No image available</div>
              )}
            </div>
            
            {allImages.length > 1 && (
              <button 
                className="carousel-nav-button next-button" 
                onClick={nextImage}
                aria-label="Next image"
              >
                &#8250;
              </button>
            )}
            
            {allImages.length > 1 && (
              <div className="carousel-indicators">
                {allImages.map((_, index) => (
                  <span
                    key={index}
                    className={`indicator-dot ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>
          
          <pre className="product-description">{product.description}</pre>
        </div>
        
        <div className="payment-section" ref={paymentSectionRef}>
          <div className="price-container">
            {product.payment_type !== 'free' && (
              <span className="original-price">${calculateFakeOriginalPrice(product.price)}</span>
            )}
            <span className="discounted-price">{displayPrice()}</span>
            {product.payment_type !== 'free' && (
              <div className="discount-badge">🔥 60% OFF - Limited Time!</div>
            )}
          </div>
          
          <div className="product-features">
            <div className="feature-row">
              <span>⏰ Full lifetime access • 📱 Access anywhere</span>
            </div>
          </div>

          <div className="product-type-category">
            <span className="type-category-item">
              <strong>{product.Type}</strong> • <strong>{product.category}</strong>
              {product.payment_type === 'subscription' && (
                <span style={{ color: '#007bff', marginLeft: '10px' }}>
                  🔁 Subscription
                </span>
              )}
              {product.payment_type === 'free' && (
                <span style={{ color: '#28a745', marginLeft: '10px' }}>
                  🆓 Free Product
                </span>
              )}
            </span>
          </div>

          {/* Checkout Button */}
          {isCreatorCapped ? (
            <div className="checkout-unavailable">
              <div className="checkout-unavailable-icon">&#9888;</div>
              <h3>Checkout is currently unavailable</h3>
              <p>
                This seller has reached their selling limit on PokeShani. Please
                check back later, or contact them directly.
              </p>
            </div>
          ) : (
            <button
              className="checkout-now-button"
              onClick={product.payment_type === 'free' ? openFreeProductModal : openCheckoutModal}
            >
              {product.payment_type === 'free' ? 'Get Free Product' : 'Checkout Now'}
            </button>
          )}

          {/* Payment Disclaimer */}
          <div className="payment-disclaimer">
            <p><strong>⚠️ Important:</strong> All payments are processed directly between buyers and sellers via PayPal. PokeShani does not hold or transfer funds. Any issues regarding payments, refunds, or disputes must be resolved directly with the seller. <Link to="/terms-of-service">See full Terms of Service</Link>.</p>
          </div>
          
          {/* Digital Delivery Disclaimer */}
          <div className="delivery-disclaimer">
            <p><strong>ℹ️ Note:</strong> Digital products are delivered automatically after your payment is confirmed by PayPal. PokeShani does not process or hold payments.</p>
          </div>
          
          {/* Made by Creator Section */}
          {creator && (
            <div className="made-by-section">
              <p>
                Made by{' '}
                <span 
                  className="creator-name"
                  onClick={() => {
                    const username = creator.email.split('@')[0];
                    window.location.href = `/store/${username}`;
                  }}
                >
                  {creator.fullName}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="checkout-modal-overlay" onClick={closeCheckoutModal}>
          <div className="checkout-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-modal-header">
              <h2>Complete Your Purchase</h2>
              <button className="checkout-modal-close" onClick={closeCheckoutModal}>×</button>
            </div>
            <div className="checkout-modal-body">
              <div className="checkout-product-info">
                {/* Thumbnail image in checkout modal */}
                <div className="checkout-product-image-wrapper">
                  <img 
                    src={product.thumbnail} 
                    alt={product.title} 
                    className="checkout-product-image" 
                  />
                </div>
                <div className="checkout-product-details">
                  <h3 className="checkout-product-title">{product.title}</h3>
                  <p className="checkout-product-type">
                    <strong>Type:</strong> {product.Type} 
                    {product.payment_type === 'subscription' ? '(Subscription)' : 
                     product.payment_type === 'free' ? '(Free Product)' : '(One-time Purchase)'}
                  </p>
                  <p className="checkout-product-category">
                    <strong>Category:</strong> {product.category}
                  </p>
                  <div className="checkout-price-section">
                    {product.payment_type !== 'free' && (
                      <span className="checkout-original-price">${calculateFakeOriginalPrice(product.price)}</span>
                    )}
                    <span className="checkout-current-price">{displayPrice()}</span>
                  </div>
                </div>
              </div>
              <div className="checkout-security-info">
                <div className="security-badge">
                  <span className="lock-icon">🔒</span>
                  <span>Secure Payment Processing</span>
                </div>
                <p className="security-description">
                  Your payment is securely processed through PayPal. All transactions are encrypted and protected.
                </p>
                <div className="platform-info">
                  <p>PokeShani is a trusted platform for digital creators and buyers.</p>
                  <div className="trust-badges">
                    <span className="trust-badge">💳 Secure Payments</span>
                    <span className="trust-badge">⚡ Instant Delivery</span>
                  </div>
                </div>
                {/* PayPal Button in Modal */}
                <div className="paypal-button-wrapper-modal">
                  {paypalLoading && <div className="loading-text">Loading payment options...</div>}
                  {paypalError && <div className="error-text">{paypalError}</div>}
                  <div ref={paypalButtonBarrier} className="paypal-button-barrier">
                    <div ref={paypalButtonContainer} className="paypal-button-protected-container">
                      <div ref={paypalButtonRef} className="paypal-button-container-modal"></div>
                    </div>
                  </div>
                </div>
                {/* PayPal Email Usage Information */}
                <div className="paypal-email-info">
                  <p className="paypal-email-description">
                    <strong>📧 PayPal Email Usage:</strong> Your PayPal email is only used for product delivery purposes. 
                    We will send your digital product access information to this email address immediately after payment confirmation. 
                    Your email will not be shared with third parties or used for marketing purposes.
                  </p>
                </div>
              </div>
            </div>
            <div className="checkout-modal-footer">
              <button className="cancel-checkout-button" onClick={closeCheckoutModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Free Product Modal */}
      {showFreeProductModal && (
        <div className="checkout-modal-overlay" onClick={closeFreeProductModal}>
          <div className="checkout-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-modal-header">
              <h2>Get Free Product</h2>
              <button className="checkout-modal-close" onClick={closeFreeProductModal}>×</button>
            </div>
            <div className="checkout-modal-body">
              <div className="checkout-product-info">
                {/* Thumbnail image in free product modal */}
                <div className="checkout-product-image-wrapper">
                  <img 
                    src={product.thumbnail} 
                    alt={product.title} 
                    className="checkout-product-image" 
                  />
                </div>
                <div className="checkout-product-details">
                  <h3 className="checkout-product-title">{product.title}</h3>
                  <p className="checkout-product-type">
                    <strong>Type:</strong> {product.Type} (Free Product)
                  </p>
                  <p className="checkout-product-category">
                    <strong>Category:</strong> {product.category}
                  </p>
                  <div className="checkout-price-section">
                    <span className="checkout-current-price">FREE</span>
                  </div>
                </div>
              </div>
              <div className="checkout-security-info">
                <div className="security-badge">
                  <span className="lock-icon">🔒</span>
                  <span>Secure Access</span>
                </div>
                <p className="security-description">
                  Please enter your email address to get instant access to this free product.
                </p>
                <div className="email-input-container">
                  <label htmlFor="freeProductEmail" className="form-label">Email Address</label>
                  <input
                    type="email"
                    id="freeProductEmail"
                    className="form-control"
                    value={freeProductEmail}
                    onChange={(e) => setFreeProductEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="checkout-modal-footer">
              <button className="cancel-checkout-button" onClick={closeFreeProductModal}>
                Cancel
              </button>
              <button 
                className="confirm-purchase-button" 
                onClick={handleFreeProductCheckout}
                disabled={!freeProductEmail || !isValidEmail(freeProductEmail)}
              >
                Get Product
              </button>
            </div>
          </div>
        </div>
      )}
      
      {!isCreatorCapped && (
        <div className="mobile-purchase-indicator" onClick={scrollToPayment}>
          Purchase Now 🚀
        </div>
      )}
    </div>
  );
};

export default SalesPage;
